import { z } from "zod";
/**
 * The three actors. These are Cognito group names, so the strings here must
 * match the groups created in modules/cognito-auth exactly.
 */
export const ROLES = ["patient", "clinician", "admin"];
export const roleSchema = z.enum(ROLES);
export function hasRole(principal, role) {
    return principal.roles.includes(role);
}
export function isAdmin(principal) {
    return hasRole(principal, "admin");
}
/**
 * API Gateway flattens `cognito:groups` on its way into the Lambda claims, and
 * it does not do so consistently: depending on the token and the number of
 * groups it arrives as a real array, as a JSON array string, or as a
 * space-separated list wrapped in square brackets (`"[admin patient]"`).
 *
 * Getting this wrong fails open in the worst possible direction - a caller
 * whose groups fail to parse looks like a caller with no groups, which is
 * safe, but a caller whose groups parse loosely could pick up a role they were
 * never granted. So: parse all three shapes, and keep only values that are
 * exactly a known role.
 */
export function parseGroups(raw) {
    const candidates = toCandidateStrings(raw);
    const known = new Set(ROLES);
    // Deduplicate - a group can legitimately appear twice across token shapes.
    return [...new Set(candidates.filter((c) => known.has(c)))];
}
function toCandidateStrings(raw) {
    if (raw === null || raw === undefined)
        return [];
    if (Array.isArray(raw)) {
        return raw.filter((v) => typeof v === "string").map((v) => v.trim());
    }
    if (typeof raw !== "string")
        return [];
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "[]")
        return [];
    // `["admin","patient"]` - a real JSON array.
    if (trimmed.startsWith("[") && trimmed.includes('"')) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.filter((v) => typeof v === "string").map((v) => v.trim());
            }
        }
        catch {
            // Fall through to the delimiter split below.
        }
    }
    // `[admin patient]` or `admin,patient` or a bare `admin`.
    return trimmed
        .replace(/^\[/, "")
        .replace(/\]$/, "")
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter((v) => v !== "");
}
//# sourceMappingURL=roles.js.map